import { useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/color-picker.index';
import { getPaletteColor } from '@/core/lib/colors';
import { useCreateOrganization } from '../hooks/useCreateOrganization';
import { useToast } from '@/shared/hooks/useToast';
import { createOrganizationSchema, type CreateOrganizationInput } from '../schemas';

interface OrganizationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function OrganizationFormDialog({ open, onOpenChange }: OrganizationFormDialogProps) {
  const { t } = useTranslation('organizations');
  const schema = createOrganizationSchema(t);
  const createOrganization = useCreateOrganization();
  const { toast } = useToast();

  // Track whether the user has manually edited the slug field
  const slugEditedRef = useRef(false);
  // Track the last auto-generated slug to distinguish auto-set from user edits
  const lastAutoSlugRef = useRef('');

  const { control, handleSubmit, reset, setValue } = useForm<CreateOrganizationInput>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', slug: '', color: 'indigo-deep' },
  });

  const selectedColor = useWatch({ name: 'color', control });
  const nameValue = useWatch({ name: 'name', control });
  const slugValue = useWatch({ name: 'slug', control });

  // Auto-generate slug from name unless the user has manually edited it
  useEffect(() => {
    if (!slugEditedRef.current) {
      const auto = generateSlug(nameValue ?? '');
      lastAutoSlugRef.current = auto;
      setValue('slug', auto, { shouldValidate: false });
    }
  }, [nameValue, setValue]);

  // Detect manual slug edits
  useEffect(() => {
    if (slugValue !== lastAutoSlugRef.current) {
      slugEditedRef.current = true;
    }
  }, [slugValue]);

  useEffect(() => {
    if (open) {
      reset({ name: '', slug: '', color: 'indigo-deep' });
      slugEditedRef.current = false;
      lastAutoSlugRef.current = '';
    }
  }, [open, reset]);

  const onSubmit = async (data: CreateOrganizationInput) => {
    try {
      await createOrganization.mutateAsync(data);
      toast.success({ title: t('toast.created') });
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  };

  const colorValue = selectedColor ? getPaletteColor(selectedColor)?.value : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{t('dialog.createTitle')}</DialogTitle>
          <DialogDescription className="sr-only">{t('dialog.description')}</DialogDescription>
        </DialogHeader>

        <form
          id="organization-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-5 py-2"
        >
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">{t('dialog.color')}</label>
              <ColorPickerPopover
                value={selectedColor}
                onSelect={(color) => setValue('color', color, { shouldValidate: true })}
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 w-10 p-0"
                    disabled={createOrganization.isPending}
                    data-testid="organizations-color-picker"
                  >
                    {colorValue && (
                      <div className="h-6 w-6 rounded-full" style={{ background: colorValue }} />
                    )}
                  </Button>
                }
              />
            </div>

            <FormFieldInput
              control={control}
              name="name"
              label={t('dialog.name')}
              floatingLabel
              disabled={createOrganization.isPending}
              data-testid="organizations-name-input"
            />
          </div>

          <div className="flex flex-col gap-1">
            <FormFieldInput
              control={control}
              name="slug"
              label={t('dialog.slug')}
              floatingLabel
              disabled={createOrganization.isPending}
              data-testid="organizations-slug-input"
            />
            <p className="text-xs text-muted-foreground">{t('dialog.slugHint')}</p>
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createOrganization.isPending}
          >
            {t('dialog.cancel')}
          </Button>
          <Button
            type="submit"
            form="organization-form"
            disabled={createOrganization.isPending}
            data-testid="organizations-submit-button"
          >
            {createOrganization.isPending ? t('dialog.submitting') : t('dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
